import { DateTime } from 'luxon';
import { Measurements, getBinLevelMeasurementOverrides } from '~/utility/BinSentry-ui-utility';
import { LitEntity, html, renderIf, nothing } from '../../utility/lit-entity';
import { BnModalMixin } from '../../common/bn-modal-mixin';
import { getIngredientBinGraphRanges } from '~/demo/ingredient-bin-graph-ranges';
import '../../common/bn-action';
import '../../common/bn-flash';
import '../../common/bn-banner';
import '../../common/bn-button';
import './bin-height-map-inspector';
import './bin-calendar-mark-manual-feed-mask';

class BinHeightMapModal extends BnModalMixin(LitEntity) {
  constructor() {
    super();
    this.id = 'bin-height-map-modal';
    this.forecast = false;
  }

  static get properties() {
    return {
      binLevelId: { type: String, attribute: 'bin-level-id' },
      measurement: { type: Object, attribute: false },
      binLevelHTML: { type: Object, attribute: false },
      binLevelDate: { type: String, attribute: false },
      imageToolsUrl: { type: String, attribute: false },
      forecast: { type: Boolean, attribute: false },
      role: { type: String, attribute: false },
    };
  }

  renderContents() {
    const name = this.entity?.properties.name || '';
    return html`
      <style>
        bn-modal#bin-height-map-modal {
          --modal-min-width: calc(100% - 2rem);
          --modal-min-height: calc(100% - 2rem);
          --modal-max-height: calc(100% - 2rem);
          --modal-body--padding: 0;
          --modal-body-display: flex;
        }
        h3 {
          margin: 0;
        }
        #bin-height-map-modal .container {
          overflow: auto;
          flex-grow: 1;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        #bin-height-map-modal bin-height-map-inspector {
          flex-grow: 1;
        }
        .head {
          display: flex;
          gap: 1em;
          align-items: baseline;
        }
        .status {
          flex-grow: 1;
          display: flex;
          justify-content: center;
          padding: 2rem;
        }
      </style>

      <h3 slot="title">${name}</h3>

      <div class="container">
        <div class="head">
          ${this._renderLevelInfo()}
        </div>

        ${this._renderBody()}

        <div class="foot">
          ${this._renderLevels()}
        </div>
      </div>
    `;
  }

  _renderLevelInfo() {
    return html`
      <h3>${this.binLevelDate || nothing}</h3>
      <h3>${this.binLevelHTML || nothing}</h3>
      ${renderIf(this.imageToolsUrl, () => html`
        <a href="/readings?binLevelId=${encodeURIComponent(this.binLevelId)}">
          <bn-button variant="text">Reading Inspector</bn-button>
        </a>
      `)}
    `;
  }

  _renderBody() {
    if (this.forecast) {
      return html`
        <div class="status">
          <bn-banner severity="info">
            Height maps are not generated for forecasted bin levels.
          </bn-banner>
        </div>
      `;
    }

    return html`
      <bin-height-map-inspector
        .href="${this.href}"
        bin-level-id="${this.binLevelId || ''}"
        @loaded-height-map="${this._onLoadedHeightMap}"
      ></bin-height-map-inspector>
    `;
  }

  _renderLevels() {
    if (!this.entity) {
      return nothing;
    }

    const { role } = this.entity.properties;
    let range;
    if (role === 'ingredient') {
      // const endTime = DateTime.now().startOf('hour').plus({ hours : 2 }).toUTC();
      range = {
        ...getIngredientBinGraphRanges(),
      };
    } else {
      range = {
        start: DateTime.now().startOf('day').toUTC().minus({ days: 14 }).toISO(),
        end: DateTime.now().endOf('day').plus({ days: 5 }).toUTC().toISO(),
      };
    }

    const contentLabel = role === 'ingredient' ? 'inventory' : 'feed';

    const selected = ({ detail: { point } }) => {
      if (!point) {
        return;
      }
      const { id: binLevelId, publishedAt, volume, weight, bulkDensity } = point;
      this.binLevelId = binLevelId;
      this.binLevelHTML = this.measurement?.html({
        volume,
        weight,
        bulkDensity,
        totalVolume: this.getProperty('volumes.fullTotal'),
      });
      this.binLevelDate = DateTime.fromISO(publishedAt).toLocaleString(DateTime.DATETIME_MED);
    };

    return html`
      <bin-level-smooth-history-graph
        href=${this.href}
        start=${range.start}
        end=${range.end}
        content-label=${contentLabel}
        highlight-selection
        highlight-manual-feed-mask
        @bn:select-data-point=${selected}
        show-raw
        hide-options
        hide-legend
        heightOverride="120px"
        small
      ></bin-level-smooth-history-graph>
    `;
  }

  async _entityChanged(entity) {
    super._entityChanged(entity);
    this.measurement = Measurements.get('binLevel', await getBinLevelMeasurementOverrides(this.entity));
    this.role = this.entity?.properties.role;
  }

  _onLoadedHeightMap({ detail: { heightMap, binLevel } }) {
    const threeDImage = heightMap.getSubEntity('https://api.binsentry.com/rel/3d-image-data');
    if (threeDImage) {
      this.binLevelId = binLevel.getLink('self').href;
      const imageToolsUrl = new URL(`${window.location.origin}/3d-image-tools`);
      imageToolsUrl.searchParams.append('binLevelId', this.binLevelId);
      this.imageToolsUrl = imageToolsUrl.href;
    }
    this._updateFieldsFromLevel(binLevel);
  }

  _updateFieldsFromLevel(binLevel) {
    if (binLevel) {
      this.binLevelId = this.getHref('self', binLevel);
      this.binLevelHTML = this.measurement?.html({
        volume: binLevel.properties.volume,
        weight: binLevel.properties.weight,
        bulkDensity: this.getProperty('bulkDensity'),
        totalVolume: this.getProperty('volumes.fullTotal'),
      });
      this.binLevelDate = DateTime
        .fromISO(binLevel.properties.publishedAt)
        .toLocaleString(DateTime.DATETIME_MED);
    }
  }
}

window.customElements.define('bin-height-map-modal', BinHeightMapModal);
